import {
  Component,
  ViewChild,
  TemplateRef,
  inject,
  signal,
  effect,
  computed,
  ChangeDetectionStrategy,
} from "@angular/core";

import { MatIconModule } from "@angular/material/icon";
import { TranslateModule } from "@ngx-translate/core";
import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import { ViewContainerRef } from "@angular/core";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { toSignal } from "@angular/core/rxjs-interop";
import { map } from "rxjs";
import { take } from "rxjs/operators";
import { LanguageFacade } from "state/language.facade";
import { SelectModalComponent } from "../select-modal/select-modal.component";

@Component({
  selector: "app-language-menu-item",
  templateUrl: "./language-menu-item.component.html",
  styleUrls: ["./language-menu-item.component.scss"],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, MatIconModule, SelectModalComponent],
})
export class LanguageMenuItemComponent {
  private readonly languageFacade = inject(LanguageFacade);
  private readonly overlay = inject(Overlay);
  private readonly vcr = inject(ViewContainerRef);
  private readonly breakpointObserver = inject(BreakpointObserver);

  @ViewChild("langPortal") langPortal!: TemplateRef<any>;
  private overlayRef?: OverlayRef;

  isLanguageDropdownOpen = signal(false);
  selectedLanguage = signal<string | null>(null);
  chosenLanguage = signal<string | null>(null);

  readonly isMobile = toSignal(
    this.breakpointObserver.observe(Breakpoints.Handset).pipe(map((r) => r.matches)),
    { initialValue: false }
  );

  languages = toSignal(this.languageFacade.languages$, { initialValue: [] });
  allLanguages = toSignal(this.languageFacade.allLanguages$, { initialValue: [] });
  activeLanguage = toSignal(this.languageFacade.activeLanguage$, { initialValue: "" });

  selectedLanguageObject = computed(() => {
    const code = this.selectedLanguage();
    return this.allLanguages().find((lang) => lang.code === code);
  });

  options = computed(() =>
    this.languages().map((lang) => ({
      code: lang.code,
      icon: this.getFlagIconPath(lang.code),
      label: lang.name,
    }))
  );

  constructor() {
    effect(() => {
      this.selectedLanguage.set(this.activeLanguage());
    });
  }

  getLabel = (code: string) =>
    this.options().find((opt) => opt.code === code)?.label ?? code;

  isSelected = (code: string) => this.selectedLanguage() === code;
  getFlagIconPath = (code: string) => `assets/flags/${code}.png`;

  openLangPortal() {
    if (this.overlayRef?.hasAttached()) return;
    this.isLanguageDropdownOpen.set(true);

    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: "cdk-overlay-dark-backdrop",
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .global()
        .centerHorizontally()
        .centerVertically(),
    });

    this.overlayRef
      .backdropClick()
      .pipe(take(1))
      .subscribe(() => this.closeLanguageModal());
    this.overlayRef.attach(new TemplatePortal(this.langPortal, this.vcr));
  }

  closeLanguageModal(event?: Event) {
    event?.stopPropagation?.();
    this.chosenLanguage.set(null);
    this.isLanguageDropdownOpen.set(false);
    this.disposeOverlay();
  }

  private disposeOverlay() {
    if (this.overlayRef) {
      this.overlayRef.detach();
      this.overlayRef.dispose();
      this.overlayRef = undefined;
    }
  }

  selectLanguage(code: string) {
    this.chosenLanguage.set(code);
  }

  cancelLanguageSelection() {
    this.closeLanguageModal();
  }

  saveLanguage() {
    const lang = this.chosenLanguage();
    if (lang) this.languageFacade.setLanguage(lang);
    this.closeLanguageModal();
  }
}
